import { useEffect, useState } from 'react';
import { fetchPureheartSettings, updatePureheartSettings } from '@/lib/api';
import type { PureHeartSettings } from '@/lib/types';

interface FormState {
  mission: string;
  vision: string;
  donation_raised: string;
  donation_goal: string;
  stats: string;
  programs: string;
}

const EMPTY: FormState = {
  mission: '',
  vision: '',
  donation_raised: '0',
  donation_goal: '15000',
  stats: '',
  programs: '',
};

export default function PureheartAdminPage() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPureheartSettings()
      .then((s: PureHeartSettings) => {
        setForm({
          mission: s.mission ?? '',
          vision: s.vision ?? '',
          donation_raised: String(s.donation_raised ?? 0),
          donation_goal: String(s.donation_goal ?? 15000),
          stats: (s.stats ?? [])
            .map((st) => `${st.label}|${st.value}|${st.prefix ?? ''}|${st.suffix ?? ''}`)
            .join('\n'),
          programs: (s.programs ?? [])
            .map(
              (p) =>
                `${p.title}|${p.icon}|${p.items.map((i) => `${i.bold} ${i.text}`).join(' ; ')}`,
            )
            .join('\n'),
        });
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const set = (key: keyof FormState, value: string) => setForm((p) => ({ ...p, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    setError(null);
    try {
      const stats = form.stats
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => {
          const parts = l.split('|').map((p) => p.trim());
          return {
            label: parts[0] ?? '',
            value: Number(parts[1]) || 0,
            prefix: parts[2] ?? '',
            suffix: parts[3] ?? '',
          };
        });

      const programs = form.programs
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => {
          const parts = l.split('|').map((p) => p.trim());
          const itemsStr = parts[2] ?? '';
          const items = itemsStr
            .split(' ; ')
            .map((item) => item.trim())
            .filter(Boolean)
            .map((item) => {
              const idx = item.indexOf(' ');
              const bold = idx >= 0 ? item.slice(0, idx + 1) : item;
              const text = idx >= 0 ? item.slice(idx + 1).trim() : '';
              return { bold, text };
            });
          return { title: parts[0] ?? '', icon: parts[1] ?? '', items };
        });

      await updatePureheartSettings({
        mission: form.mission,
        vision: form.vision,
        donation_raised: Number(form.donation_raised) || 0,
        donation_goal: Number(form.donation_goal) || 15000,
        stats,
        programs,
      });
      setMessage('PureHeart settings saved successfully.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-foreground-500">
        <i className="ri-loader-4-line animate-spin text-2xl inline-block" aria-hidden="true" />
      </div>
    );
  }

  const inputClass =
    'w-full px-3.5 py-2.5 rounded-md text-sm border border-background-300 bg-white outline-none focus:ring-2 focus:ring-primary-300 text-foreground-900';
  const labelClass = 'block text-xs font-label font-medium text-foreground-700 mb-1.5';

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h2 className="font-heading font-semibold text-xl text-foreground-900">PureHeart Program</h2>
        <p className="text-sm text-foreground-500 mt-1">
          Manage the PureHeart Africa NGO page — stats, mission, vision, programs and donation goal.
        </p>
      </div>

      {message && (
        <div className="mb-4 px-4 py-3 rounded-md bg-accent-100 text-accent-900 text-sm border border-accent-300">
          {message}
        </div>
      )}
      {error && (
        <div className="mb-4 px-4 py-3 rounded-md bg-red-50 text-red-600 text-sm border border-red-200">
          {error}
        </div>
      )}

      <div className="space-y-6">
        <section className="bg-white rounded-lg border border-background-200 p-6 space-y-4">
          <h3 className="flex items-center gap-2 font-heading font-semibold text-base text-foreground-900">
            <i className="ri-heart-line text-primary-600" aria-hidden="true" /> Mission & Vision
          </h3>
          <div>
            <label className={labelClass}>Mission</label>
            <textarea value={form.mission} onChange={(e) => set('mission', e.target.value)} className={`${inputClass} resize-y`} rows={3} />
          </div>
          <div>
            <label className={labelClass}>Vision</label>
            <textarea value={form.vision} onChange={(e) => set('vision', e.target.value)} className={`${inputClass} resize-y`} rows={3} />
          </div>
        </section>

        <section className="bg-white rounded-lg border border-background-200 p-6 space-y-4">
          <h3 className="flex items-center gap-2 font-heading font-semibold text-base text-foreground-900">
            <i className="ri-funds-line text-primary-600" aria-hidden="true" /> Donation Goal
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Amount Raised ($)</label>
              <input type="number" value={form.donation_raised} onChange={(e) => set('donation_raised', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className={labelClass}>Goal ($)</label>
              <input type="number" value={form.donation_goal} onChange={(e) => set('donation_goal', e.target.value)} className={inputClass} />
            </div>
          </div>
        </section>

        <section className="bg-white rounded-lg border border-background-200 p-6 space-y-4">
          <h3 className="flex items-center gap-2 font-heading font-semibold text-base text-foreground-900">
            <i className="ri-bar-chart-line text-primary-600" aria-hidden="true" /> Statistics
          </h3>
          <div>
            <label className={labelClass}>Stats (one per line: Label|Value|Prefix|Suffix)</label>
            <textarea
              value={form.stats}
              onChange={(e) => set('stats', e.target.value)}
              className={`${inputClass} resize-y`}
              rows={5}
              placeholder={'Success Stories|10|0|\nChildren Helped|150||'}
            />
          </div>
        </section>

        <section className="bg-white rounded-lg border border-background-200 p-6 space-y-4">
          <h3 className="flex items-center gap-2 font-heading font-semibold text-base text-foreground-900">
            <i className="ri-list-check-3 text-primary-600" aria-hidden="true" /> Programs
          </h3>
          <div>
            <label className={labelClass}>
              Programs (one per line: Title|Icon|Bold: text ; Bold: text ; Bold: text)
            </label>
            <textarea
              value={form.programs}
              onChange={(e) => set('programs', e.target.value)}
              className={`${inputClass} resize-y`}
              rows={10}
              placeholder={'Education|ri-graduation-cap-line|Scholarship Programs: text ; School Construction: text'}
            />
            <p className="mt-1 text-xs text-foreground-400">
              Each program uses a Remix Icon name (e.g. ri-graduation-cap-line, ri-hospital-line).
            </p>
          </div>
        </section>

        <div className="flex justify-end">
          <button type="button" onClick={handleSave} disabled={saving} className="btn-primary whitespace-nowrap">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}